import { useCallback } from 'react';
import { isEscapePressed } from 'utils';
import useKeyDown from 'hooks/useKeyDown';
import useEscapeLayer, { EscapeLayerLevel } from 'hooks/useEscapeLayer';
import { EventListenerConfig } from './useAddEventListener';

type UseCloseOnEscapeOptions = EventListenerConfig & {
  /**
   * `overlay` for a modal, `base` (default) for anything living in the page -
   * a drawer, a panel, an inline banner. Escape only reaches a layer while
   * nothing is stacked on top of it, so a popup opened over a drawer closes on
   * the first press and the drawer on the second.
   */
  level?: EscapeLayerLevel;
  /**
   * The layer's own overlay node, `ModalRef.container` for a modal. Pass it
   * for an `overlay` layer, so a modal opened over this one - a confirmation,
   * a form - takes the first press for itself.
   */
  element?: Element;
};

export default function useCloseOnEscape(
  onClose: () => void,
  options: UseCloseOnEscapeOptions = {},
) {
  const {
    enabled = true,
    capture = false,
    level = 'base',
    element,
  } = options;

  const isTopEscapeLayer = useEscapeLayer({ enabled, level, element });

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isEscapePressed(e) || !isTopEscapeLayer()) return;

      e.preventDefault();
      e.stopPropagation();

      // don't remove settimeout
      setTimeout(() => onClose(), 0);
    },
    [onClose, isTopEscapeLayer],
  );

  useKeyDown({ onKeyDown, capture, enabled });
}
